<template>
  <!-- Busca nas mensagens -->
  <div v-if="msgSearchOpen" class="mv-msg-search-bar">
    <input
      ref="msgSearchInputEl"
      v-model="msgSearchQuery"
      class="mv-msg-search-input"
      placeholder="Buscar nas mensagens…"
      @keydown.enter="doMsgSearch"
      @keydown.escape="closeMsgSearch"
    />
    <span v-if="msgSearchActive" class="mv-msg-search-count">
      {{ msgMatchIds.length ? (msgCurrentIdx + 1) + '/' + msgMatchIds.length : '0 resultados' }}
    </span>
    <button v-if="msgMatchIds.length" class="mv-msg-search-nav" @click="goPrevMsg" title="Anterior">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
    </button>
    <button v-if="msgMatchIds.length" class="mv-msg-search-nav" @click="goNextMsg" title="Próximo">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
    </button>
    <button class="mv-msg-search-close" @click="closeMsgSearch">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
  </div>

  <!-- Messages area -->
  <div class="mv-msgs" ref="msgsEl" @scroll="onMsgsScroll">
    <div v-if="loadingMsgs || msgSearchLoading" class="mv-msgs-loading">
      <div class="mv-spinner"></div>
    </div>
    <template v-else>
      <div v-if="loadingMore" class="mv-load-more-row">
        <div class="mv-spinner-sm"></div>
      </div>
      <div v-else-if="noMoreMsgs" class="mv-no-more">início da conversa</div>
      <div
        v-for="msg in processedMsgs"
        :key="msg.id"
        class="mv-msg-row"
        :class="{ out: msg.message_type === 1, inn: msg.message_type === 0, act: msg.message_type === 2, 'msg-match-current': msgSearchActive && msgMatchIds[msgCurrentIdx] === msg.id }"
        :data-msg-id="msg.id"
      >
        <div v-if="msg.message_type === 2" class="mv-msg-act">{{ msg.content }}</div>
        <template v-else>
          <div
            v-if="msg.showAvatar"
            class="mv-msg-av"
            :style="{ background: avatarColor(senderName(msg)) }"
          >
            <img
              v-if="msg.sender?.thumbnail && msg.message_type === 1"
              :src="msg.sender.thumbnail"
              :alt="senderName(msg)"
              @error="(e) => (e.target as HTMLImageElement).style.display='none'"
            />
            <span v-else>{{ initials(senderName(msg)) }}</span>
          </div>
          <div v-else class="mv-msg-av-spacer"></div>

          <div class="mv-bubble" :class="{ 'mv-bubble--priv': msg.private }">
            <span v-if="msg.private" class="mv-priv-tag">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Nota interna
            </span>
            <span v-if="msg.content" class="mv-bubble-text" v-html="highlightMsgHtml(msg.content, msg.message_type === 1, messageSignature)"></span>
            <template v-if="msg.attachments?.length">
              <div v-for="att in msg.attachments" :key="att.id" class="mv-att">
                <a v-if="att.file_type === 'image'" :href="att.data_url" target="_blank" rel="noopener">
                  <img :src="att.thumb_url || att.data_url" class="mv-att-img" alt="imagem" loading="lazy" />
                </a>
                <a v-else :href="att.data_url" target="_blank" rel="noopener" class="mv-att-file">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {{ att.extension?.toUpperCase() || 'Arquivo' }}
                </a>
              </div>
            </template>
            <span class="mv-bubble-ts">
              {{ fmtTs(msg.created_at) }}
              <span v-if="msg.message_type === 1" class="mv-msg-status" :class="'s-'+(msg.status||'sent')" :title="msg.status === 'read' ? 'Lido' : msg.status === 'delivered' ? 'Entregue' : msg.status === 'failed' ? 'Falhou' : 'Enviado'">
                <svg v-if="msg.status === 'failed'" width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="1" x2="7" y2="7"/><line x1="7" y1="1" x2="1" y2="7"/></svg>
                <svg v-else-if="msg.status === 'read' || msg.status === 'delivered'" width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/><polyline points="5,4 7.5,6.5 13,1"/></svg>
                <svg v-else width="10" height="8" viewBox="0 0 10 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/></svg>
              </span>
            </span>
          </div>
        </template>
      </div>
    </template>
  </div>

  <!-- Banner: janela de 24h de mensagens fechada -->
  <div v-if="windowClosed" class="mv-window-banner">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    <span>Janela de 24h do WhatsApp encerrada — responda pelo desktop com um template</span>
  </div>

  <!-- Composer -->
  <div class="mv-composer" :class="{ 'mv-composer--priv': notaInterna }">
    <label class="mv-attach-btn" :class="{ 'mv-attach-disabled': windowClosed || notaInterna }" title="Anexar">
      <input type="file" multiple hidden ref="fileInputEl" :disabled="windowClosed || notaInterna" @change="onFilePick" />
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
      </svg>
    </label>
    <button
      v-if="can('messages.privateNote')"
      class="mv-priv-btn"
      :class="{ 'mv-priv-btn--active': notaInterna }"
      :disabled="windowClosed"
      @click="notaInterna = !notaInterna"
      :title="notaInterna ? 'Nota interna ativada' : 'Marcar como nota interna'"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
    </button>
    <div class="mv-ta-wrap">
      <div v-if="files.length" class="mv-files-preview">
        <span v-for="(f, i) in files" :key="i" class="mv-file-pill">
          {{ f.name.slice(0, 20) }}{{ f.name.length > 20 ? '…' : '' }}
          <button @click="files.splice(i, 1)">×</button>
        </span>
      </div>
      <textarea
        ref="taEl"
        v-model="draft"
        class="mv-ta"
        :placeholder="notaInterna ? 'Nota interna — só a equipe vê…' : (windowClosed ? 'Só é possível responder com template' : 'Mensagem…')"
        :disabled="windowClosed"
        rows="1"
        @keydown.enter.exact.prevent="sendMsg"
        @input="growTa"
      ></textarea>
    </div>
    <button class="mv-send-btn" @click="sendMsg" :disabled="!canSend || sending || windowClosed">
      <span v-if="sending" class="mv-spinner-sm"></span>
      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ProcessedMsg, ChatMessage } from '@/types/chat'
import { avatarColor, initials } from '@/composables/useHelpers'
import { fmtTs, senderName } from '@/utils/mobileFormat'
import { useMessageSearch } from '@/composables/useMessageSearch'
import { useMessageSender } from '@/composables/useMessageSender'
import { usePermission } from '@/composables/usePermission'

const { can } = usePermission()

const props = defineProps<{
  processedMsgs: ProcessedMsg[]
  loadingMsgs: boolean
  loadingMore: boolean
  noMoreMsgs: boolean
  messageSignature: string
  convId: number | null
  token: string
  contactPhone: string | null | undefined
  loadMoreMsgs: () => Promise<void>
  windowClosed: boolean
  assigneeId: number | null
  myAgentId: number
  labels: string[]
  status: string | null | undefined
}>()

const emit = defineEmits<{
  'message-created': [msg: ChatMessage, convId: number]
  'auto-assigned': [convId: number, agentId: number]
  'labels-changed': [convId: number, labels: string[]]
  'status-changed': [convId: number, status: string]
}>()

const msgsEl      = ref<HTMLElement | null>(null)
const taEl        = ref<HTMLTextAreaElement | null>(null)
const fileInputEl = ref<HTMLInputElement | null>(null)
const draft       = ref('')
const files       = ref<File[]>([])
const notaInterna = ref(false)

const canSend = computed(() => draft.value.trim().length > 0 || files.value.length > 0)

// Notas internas exigem a janela aberta (mesmo composer da mensagem normal) — se a janela
// fechar enquanto o toggle está ativo, desativa pra não deixar o composer num estado inválido.
watch(() => props.windowClosed, (closed) => { if (closed) notaInterna.value = false })

const processedMsgsRef = computed(() => props.processedMsgs)
const noMoreMsgsLocal   = ref(props.noMoreMsgs)
watch(() => props.noMoreMsgs, v => { noMoreMsgsLocal.value = v })

const {
  msgSearchOpen, msgSearchQuery, msgSearchActive, msgSearchLoading, msgCurrentIdx, msgSearchInputEl,
  msgMatchIds, getSig, highlightMsgHtml,
  openMsgSearch, closeMsgSearch, doMsgSearch, goNextMsg, goPrevMsg,
} = useMessageSearch({
  processedMsgs: processedMsgsRef,
  msgsEl,
  messageSignature: () => props.messageSignature,
  loadMoreMsgs: props.loadMoreMsgs,
  noMoreMsgs: noMoreMsgsLocal,
})

function onMsgsScroll() {
  if (msgsEl.value && msgsEl.value.scrollTop < 60) props.loadMoreMsgs()
}

function scrollBottom() {
  if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight
}

const { sending, sendMsg } = useMessageSender({
  getConvId: () => props.convId,
  getToken: () => props.token,
  getContactPhone: () => props.contactPhone,
  getSig,
  draft,
  files,
  isPrivate: notaInterna,
  fileInputEl,
  taEl,
  scrollBottom,
  getAssigneeId: () => props.assigneeId,
  getMyAgentId: () => props.myAgentId,
  getLabels: () => props.labels,
  getStatus: () => props.status,
  onMessageCreated: (msg, convId) => emit('message-created', msg, convId),
  onAutoAssigned: (convId, agentId) => emit('auto-assigned', convId, agentId),
  onLabelsChanged: (convId, labels) => emit('labels-changed', convId, labels),
  onStatusChanged: (convId, status) => emit('status-changed', convId, status),
})

function onFilePick(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files) { files.value.push(...Array.from(input.files)); input.value = '' }
}

function growTa() {
  const el = taEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 100) + 'px'
}

defineExpose({ openMsgSearch, scrollBottom, getScrollEl: () => msgsEl.value })
</script>

<style scoped>
.mv-spinner {
  width: 32px; height: 32px;
  border: 3px solid #E5E7EB;
  border-top-color: #BC0C24;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ══ Messages ══ */
.mv-msgs {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #F7F7F7;
}

.mv-load-more-row { display: flex; justify-content: center; padding: 10px 0; }
.mv-no-more { text-align: center; font-size: 11px; color: #bbb; padding: 8px 0; }

.mv-msgs-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mv-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-bottom: 2px;
}
.mv-msg-row.out  { flex-direction: row-reverse; }
.mv-msg-row.act  { justify-content: center; }

.mv-msg-av {
  width: 28px; height: 28px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 800;
  color: #fff; overflow: hidden; flex-shrink: 0;
}
.mv-msg-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.mv-msg-av-spacer { width: 28px; flex-shrink: 0; }

.mv-msg-act {
  font-size: 11px;
  color: #9CA3AF;
  background: rgba(0,0,0,.05);
  padding: 3px 10px;
  border-radius: 20px;
  max-width: 85%;
  text-align: center;
}

.mv-bubble {
  max-width: 75%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.inn .mv-bubble {
  background: #fff;
  border-radius: 0 14px 14px 14px;
  padding: 8px 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}
.out .mv-bubble {
  background: #BC0C24;
  border-radius: 14px 0 14px 14px;
  padding: 8px 12px;
  box-shadow: 0 1px 3px rgba(188,12,36,.3);
}

.mv-bubble-text {
  font-size: 13.5px;
  line-height: 1.45;
  word-break: break-word;
}
.inn .mv-bubble-text { color: #1A1A1A; }
.out .mv-bubble-text { color: #fff; }

.mv-bubble--priv, .mv-bubble--priv.out { background: #FEF3C7 !important; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
.mv-bubble--priv .mv-bubble-text { color: #78350F !important; }
.mv-priv-tag { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #92400E; }
.mv-bubble--priv .mv-bubble-ts { color: rgba(120,53,15,.6) !important; }
.out .mv-bubble-text .msg-sig {
  display: block;
  margin-top: 7px;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.28);
  font-size: 10.5px;
  font-style: italic;
  opacity: 0.7;
  letter-spacing: 0.01em;
}

.mv-bubble-ts {
  font-size: 10px;
  align-self: flex-end;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.inn .mv-bubble-ts { color: #9CA3AF; }
.out .mv-bubble-ts { color: rgba(255,255,255,.65); }
.mv-msg-status { display: inline-flex; align-items: center; flex-shrink: 0; }
.out .mv-msg-status.s-read   { color: #93C5FD; }
.out .mv-msg-status.s-failed { color: #FECACA; }
.inn .mv-msg-status.s-read   { color: #3B82F6; }
.inn .mv-msg-status.s-failed { color: #EF4444; }

.mv-att { margin-top: 4px; }
.mv-att-img {
  max-width: 200px;
  border-radius: 8px;
  display: block;
}
.mv-att-file {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; padding: 4px 8px;
  border-radius: 8px;
  background: rgba(0,0,0,.06);
  color: inherit; text-decoration: none;
}

/* ══ Composer ══ */
.mv-composer {
  background: #fff;
  border-top: 1px solid #E5E7EB;
  padding: 8px 12px;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  transition: background .15s, border-color .15s;
}
.mv-composer--priv { background: #FFFBEB; border-top-color: #FDE68A; }

.mv-attach-btn {
  color: #9CA3AF;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; flex-shrink: 0;
}
.mv-attach-btn.mv-attach-disabled { opacity: .4; cursor: not-allowed; }

.mv-priv-btn {
  color: #9CA3AF; background: none; border: none;
  cursor: pointer; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; flex-shrink: 0;
  transition: background .15s, color .15s;
}
.mv-priv-btn:hover { background: rgba(0,0,0,.05); color: #92400E; }
.mv-priv-btn:disabled { opacity: .4; cursor: not-allowed; }
.mv-priv-btn--active, .mv-priv-btn--active:hover { background: #F59E0B; color: #fff; }

.mv-window-banner {
  display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 8px 14px; background: #fef2f2;
  border-top: 1px solid #fecaca;
  font-size: 11.5px; font-weight: 600; color: #b91c1c;
  flex-shrink: 0; text-align: center;
}
.mv-window-banner svg { flex-shrink: 0; color: #dc2626; }

.mv-ta-wrap {
  flex: 1;
  background: #F3F4F6;
  border-radius: 20px;
  padding: 8px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: background .15s;
}
.mv-composer--priv .mv-ta-wrap { background: #FEF3C7; }

.mv-ta {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  font-family: 'Poppins', sans-serif;
  color: #1A1A1A;
  resize: none;
  max-height: 100px;
  line-height: 1.4;
}
.mv-ta::placeholder { color: #9CA3AF; }
.mv-ta:disabled { color: #9CA3AF; cursor: not-allowed; }

.mv-files-preview {
  display: flex; flex-wrap: wrap; gap: 4px;
}
.mv-file-pill {
  font-size: 11px;
  background: rgba(188,12,36,.1);
  color: #BC0C24;
  border-radius: 20px;
  padding: 2px 8px;
  display: flex; align-items: center; gap: 4px;
}
.mv-file-pill button {
  background: none; border: none; cursor: pointer; color: inherit;
  font-size: 13px; line-height: 1; padding: 0;
}

.mv-send-btn {
  width: 40px; height: 40px;
  border-radius: 50%;
  background: #BC0C24;
  color: #fff;
  border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s;
}
.mv-send-btn:disabled { background: #E5E7EB; color: #9CA3AF; }
.mv-send-btn:not(:disabled):active { background: #73010F; }

.mv-spinner-sm {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}

/* ── Message search ── */
.mv-msg-search-bar {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; background: #fafafa;
  border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
}
.mv-msg-search-input {
  flex: 1; border: 1px solid #d1d5db; border-radius: 8px;
  padding: 6px 10px; font-size: 13px; outline: none;
  background: #fff; color: #111827;
}
.mv-msg-search-input:focus { border-color: #BC0C24; }
.mv-msg-search-count {
  font-size: 11px; color: #6b7280; white-space: nowrap; flex-shrink: 0;
}
.mv-msg-search-nav,
.mv-msg-search-close {
  background: none; border: none; cursor: pointer; padding: 4px 5px;
  color: #6b7280; border-radius: 6px; display: flex; align-items: center; flex-shrink: 0;
}
.mv-msg-search-nav:hover,
.mv-msg-search-close:hover { background: #f3f4f6; color: #111827; }
.msg-match-current { outline: 2px solid #f59e0b; outline-offset: -1px; border-radius: 10px; }
mark.msg-hl { background: #fef08a; color: #78350f; border-radius: 2px; padding: 0 1px; }
</style>
